// src/components/DocumentUpload.js
import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader, AlertCircle, Upload, FileText, X, Check, Camera } from 'lucide-react';
import { Toaster, toast } from 'react-hot-toast';

const Button = ({ onClick, children, className, disabled, type = "button" }) => (
    <button onClick={onClick} className={className} disabled={disabled} type={type}>
        {children}
    </button>
);

export default function DocumentUpload() {
    const { id: serviceId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
   
    const [service, setService] = useState(location.state?.service || null);
    const [userData, setUserData] = useState(location.state?.userData || null);
    const [isLoading, setIsLoading] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [error, setError] = useState('');
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [documentChecklist, setDocumentChecklist] = useState([]);

    const [description, setDescription] = useState('');

    const [uploadedDocTypes, setUploadedDocTypes] = useState(new Set());


    const [showCameraModal, setShowCameraModal] = useState(false);
    const [cameraStream, setCameraStream] = useState(null);
    const [capturedPhoto, setCapturedPhoto] = useState(null);
    const videoRef = React.useRef(null);
    const canvasRef = React.useRef(null);

    useEffect(() => {
        if (documentChecklist.length === 0) {
            setDescription('');
            return;
        }

        const uploaded = documentChecklist
            .filter(doc => uploadedDocTypes.has(doc.document_type))
            .sort((a, b) => a.display_order - b.display_order)
            .map(doc => doc.document_type);

        const missing = documentChecklist
            .filter(doc => !uploadedDocTypes.has(doc.document_type))
            .sort((a, b) => a.display_order - b.display_order)
            .map(doc => doc.document_type);

        let desc = '';

        if (uploaded.length > 0) {
            desc += 'Documenti Caricati:\n';
            uploaded.forEach((docType, index) => {
                desc += `${index + 1}. ${docType}\n`;
            });
            desc += '\n';
        }

        if (missing.length > 0) {
            desc += 'Documenti Mancanti:\n';
            missing.forEach((docType, index) => {
                desc += `${index + 1}. ${docType}\n`;
            });
        }

        setDescription(desc);
        console.log(description)
    }, [uploadedDocTypes, documentChecklist]);

    useEffect(() => {
        const consent = localStorage.getItem('cookieConsent');
        let token;

        if (consent === 'true') {
            token = localStorage.getItem('jwtToken');
        } else {
            token = sessionStorage.getItem('jwtToken');
        }
        if (!token) {
            toast.error("Accedi per visualizzare questa pagina..");
            navigate('/login', { state: { from: location.pathname } });
            return;
        }

        // Redirect back if no service or user data
        if (!service || !userData) {
            toast.error("Per favore inizia dall’inizio.");
            navigate(`/services/${serviceId}/user-data`);
            return;
        }

        loadDocumentChecklist()
        loadExistingDocuments();
    }, [serviceId, service, userData, location.pathname, navigate]);

    const startCamera = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: 'environment' }
            });
            setCameraStream(stream);
            setShowCameraModal(true);

            // Wait for modal to render, then set video stream
            setTimeout(() => {
                if (videoRef.current) {
                    videoRef.current.srcObject = stream;
                    videoRef.current.play();
                }
            }, 100);
        } catch (err) {
            console.error('Error accessing camera:', err);
            toast.error('Could not access camera. Please check permissions.');
        }
    };

    const stopCamera = () => {
        if (cameraStream) {
            cameraStream.getTracks().forEach(track => track.stop());
            setCameraStream(null);
        }
        setShowCameraModal(false);
        setCapturedPhoto(null);
    };

    const capturePhoto = () => {
        const video = videoRef.current;
        const canvas = canvasRef.current;

        if (video && canvas) {
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(video, 0, 0);

            canvas.toBlob((blob) => {
                setCapturedPhoto(blob);
                if (cameraStream) {
                    cameraStream.getTracks().forEach(track => track.stop());
                }
            }, 'image/jpeg', 0.95);
        }
    };

    const retakePhoto = async () => {
        setCapturedPhoto(null);
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: 'environment' }
            });
            setCameraStream(stream);

            setTimeout(() => {
                if (videoRef.current) {
                    videoRef.current.srcObject = stream;
                    videoRef.current.play();
                }
            }, 100);
        } catch (err) {
            console.error('Error accessing camera:', err);
            toast.error('Could not access camera. Please check permissions.');
        }
    };

    const uploadCapturedPhoto = async () => {
        if (!capturedPhoto) return;

        setIsUploading(true);
        const consent = localStorage.getItem('cookieConsent');
        let token = consent === 'true'
            ? localStorage.getItem('jwtToken')
            : sessionStorage.getItem('jwtToken');

        try {
            const formData = new FormData();
            const fileName = `photo_${Date.now()}.jpg`;
            formData.append('document', capturedPhoto, fileName);
            formData.append('documentType', 'additional');
            formData.append('serviceId', serviceId);

            const response = await axios.post(
                'https://api.cafpraticheonline.it/api/documents/upload',
                formData,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );

            const newFile = {
                id: response.data.document_id,
                name: fileName,
                size: capturedPhoto.size,
                uploadedAt: new Date().toISOString()
            };

            setUploadedFiles(prev => [...prev, newFile]);
            toast.success('Foto caricata con successo!');
            stopCamera();
        } catch (err) {
            console.error('Error uploading photo:', err);
            toast.error('Failed to upload photo. Please try again.');
        } finally {
            setIsUploading(false);
        }
    };
    const loadDocumentChecklist = async () => {
        const consent = localStorage.getItem('cookieConsent');
        let token = consent === 'true'
            ? localStorage.getItem('jwtToken')
            : sessionStorage.getItem('jwtToken');

        try {
            const response = await axios.get(
                `https://api.cafpraticheonline.it/api/documents/checklist/${serviceId}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );
            console.log(response.data.results)
            setDocumentChecklist(response.data.results);
        } catch (err) {
            console.error('Error loading document checklist:', err);
            // If no required documents are set up, just load existing documents
            loadExistingDocuments();
        }
    };

    const loadExistingDocuments = async () => {
        const consent = localStorage.getItem('cookieConsent');
        let token;

        if (consent === 'true') {
            token = localStorage.getItem('jwtToken');
        } else {
            token = sessionStorage.getItem('jwtToken');
        }

        try {
            const response = await axios.get(
                `https://api.cafpraticheonline.it/api/documents/service/${serviceId}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            const documents = response.data.map(doc => ({
                id: doc.document_id,
                name: doc.file_name,
                size: doc.file_size,
                uploadedAt: doc.created_at
            }));

            setUploadedFiles(documents);
        } catch (err) {
            console.error('Error loading documents:', err);
        }
    };

    const handleDocumentTypeToggle = (docType) => {
        setUploadedDocTypes(prev => {
            const newSet = new Set(prev);
            if (newSet.has(docType)) {
                newSet.delete(docType);
            } else {
                newSet.add(docType);
            }
            return newSet;
        });
    };

    const handleFileChange = async (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        // Validate file types and sizes
        const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
        const maxSize = 5 * 1024 * 1024; // 5MB

        for (const file of files) {
            if (!allowedTypes.includes(file.type)) {
                toast.error(`Tipo di file non valido ${file.name}. Carica file JPG, PNG o PDF.`);
                return;
            }
            if (file.size > maxSize) {
                toast.error(`Il file è troppo grande. La dimensione massima è 5MB.`);
                return;
            }
        }

        setIsUploading(true);
        const consent = localStorage.getItem('cookieConsent');
        let token;

        if (consent === 'true') {
            token = localStorage.getItem('jwtToken');
        } else {
            token = sessionStorage.getItem('jwtToken');
        }

        try {
            const uploadPromises = files.map(async (file) => {
                const formData = new FormData();
                formData.append('document', file);
                // FIXED: Changed from 'general' to 'additional' to match backend validation
                formData.append('documentType', 'additional');
                formData.append('serviceId', serviceId);

                console.log('Uploading file:', file.name, 'to service:', serviceId);

                const response = await axios.post(
                    'https://api.cafpraticheonline.it/api/documents/upload',
                    formData,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            'Content-Type': 'multipart/form-data'
                        }
                    }
                );

                console.log('Upload response:', response.data);

                return {
                    id: response.data.document_id,
                    name: file.name,
                    size: file.size,
                    uploadedAt: new Date().toISOString()
                };
            });

            const uploadResults = await Promise.all(uploadPromises);
            setUploadedFiles(prev => [...prev, ...uploadResults]);
            toast.success(`${files.length} document(s) uploaded successfully!`);

        } catch (err) {
            console.error('Error uploading documents:', err);
            console.error('Error response:', err.response?.data);

            // More detailed error handling
            if (err.response?.data?.message) {
                toast.error(`Upload failed: ${err.response.data.message}`);
            } else {
                toast.error('Caricamento fallito. Riprova.');
            }
        } finally {
            setIsUploading(false);
            // Clear the input
            e.target.value = '';
        }
    };

    const removeDocument = async (documentId) => {
        const consent = localStorage.getItem('cookieConsent');
        let token;

        if (consent === 'true') {
            token = localStorage.getItem('jwtToken');
        } else {
            token = sessionStorage.getItem('jwtToken');
        }

        try {
            await axios.delete(`https://api.cafpraticheonline.it/api/documents/${documentId}`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            setUploadedFiles(prev => prev.filter(file => file.id !== documentId));
            toast.success('Documento rimosso con successo!');
        } catch (err) {
            console.error('Error removing document:', err);
            toast.error('Impossibile rimuovere il documento. Riprova.');
        }
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const handleProceedToPayment = () => {
        // You can add a minimum document requirement if needed
        if (uploadedFiles.length === 0) {
            toast.error('Carica almeno un documento prima di procedere.');
            return;
        }

        // Navigate to the payment page
        navigate(`/services/${serviceId}/payment`, {
            state: {
                service,
                userData,
                documents: uploadedFiles,
                description: description
            }
        });
    };

    if (!service || !userData) {
        return (
            <div className="min-h-screen flex items-center justify-center flex-col">
                <Loader className="animate-spin text-yellow-500 h-10 w-10 mb-4" />
                <p className="text-gray-600">Caricamento…</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center flex-col text-red-600">
                <AlertCircle className="h-10 w-10 mb-4" />
                <p>{error}</p>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 py-8 px-4">
            <div className="max-w-4xl mx-auto">
                {/* Service Info Header */}
                <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
                    <h1 className="text-2xl font-bold text-gray-800 mb-2">{service.title}</h1>
                    <p className="text-gray-600 mb-2">
                        Prezzo: <span className="font-semibold text-yellow-600">€{service.price}</span>
                    </p>
                    <p className="text-sm text-gray-500">
                        Cliente: {userData.firstName} {userData.lastName}
                    </p>
                </div>

                {/* Document Upload Section */}
                <div className="bg-white rounded-2xl shadow-lg p-8">
                    <h2 className="text-xl font-bold text-gray-800 mb-6">Carica Documenti</h2>
                    <p className="text-gray-600 mb-8">
                        Carica i tuoi documenti per procedere con la richiesta del servizio.
                        Puoi caricare più documenti contemporaneamente. Tutti i documenti devono essere chiari e leggibili.
                    </p>
                    {/* Document Checklist */}
                    {documentChecklist.length > 0 && (
                        <div className="mb-6">
                            <h3 className="text-lg font-semibold text-gray-800 mb-4">Elenco dei Documenti Richiesti</h3>

                            {/* Uploaded Documents */}
                            {Array.from(uploadedDocTypes).length > 0 && (
                                <div className="mb-6">
                                    <div className="flex items-center mb-3">
                                        <Check className="h-5 w-5 text-green-600 mr-2" />
                                        <h4 className="font-semibold text-green-700">Documenti Caricati</h4>
                                    </div>
                                    <div className="space-y-2 pl-7">
                                        {documentChecklist
                                            .filter(doc => uploadedDocTypes.has(doc.document_type))
                                            .sort((a, b) => a.display_order - b.display_order)
                                            .map((doc) => (
                                                <div key={doc.requirement_id} className="flex items-start">
                                                    <input
                                                        type="checkbox"
                                                        checked={true}
                                                        onChange={() => handleDocumentTypeToggle(doc.document_type)}
                                                        className="mt-1 h-4 w-4 text-green-600 rounded focus:ring-green-500"
                                                    />
                                                    <label className="ml-3 text-sm text-gray-700 cursor-pointer flex-1">
                                                        {doc.document_type}
                                                        {doc.is_mandatory && (
                                                            <span className="text-red-500 ml-1">*</span>
                                                        )}
                                                    </label>
                                                </div>
                                            ))}
                                    </div>
                                </div>
                            )}

                            {/* Missing Documents */}
                            <div>
                                <div className="flex items-center mb-3">
                                    <AlertCircle className="h-5 w-5 text-amber-600 mr-2" />
                                    <h4 className="font-semibold text-amber-700">Documenti Mancanti</h4>
                                </div>
                                <div className="space-y-2 pl-7">
                                    {documentChecklist
                                        .filter(doc => !uploadedDocTypes.has(doc.document_type))
                                        .sort((a, b) => a.display_order - b.display_order)
                                        .map((doc) => (
                                            <div key={doc.requirement_id} className="flex items-start">
                                                <input
                                                    type="checkbox"
                                                    checked={false}
                                                    onChange={() => handleDocumentTypeToggle(doc.document_type)}
                                                    className="mt-1 h-4 w-4 text-gray-400 rounded focus:ring-yellow-500"
                                                />
                                                <label className="ml-3 text-sm text-gray-600 cursor-pointer flex-1">
                                                    {doc.document_type}
                                                    {doc.is_mandatory && (
                                                        <span className="text-red-500 ml-1">*</span>
                                                    )}
                                                </label>
                                            </div>
                                        ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Single Upload Area */}
                    <div className="border rounded-lg p-6 mb-6"></div>

                    {/* Single Upload Area */}
                    <div className="border rounded-lg p-6 mb-6">
                        <div className="flex items-center mb-4">
                            <FileText className="h-5 w-5 text-gray-500 mr-2" />
                            <span className="font-semibold text-gray-800">
                                Carica Documenti
                            </span>
                            {uploadedFiles.length > 0 && (
                                <div className="ml-auto flex items-center text-green-600">
                                    <Check className="h-5 w-5 mr-1" />
                                    <span className="text-sm">{uploadedFiles.length} documento(i) caricati</span>
                                </div>
                            )}
                        </div>

                        {/* File Upload Area */}
                        {/* File Upload and Camera Options */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            {/* Upload Files */}
                            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
                                <Upload className="h-10 w-10 text-gray-400 mx-auto mb-4" />
                                <p className="text-gray-600 mb-4">Seleziona file dal dispositivo</p>
                                <input
                                    type="file"
                                    multiple
                                    accept=".jpg,.jpeg,.png,.pdf"
                                    onChange={handleFileChange}
                                    className="hidden"
                                    id="file-upload"
                                    disabled={isUploading}
                                />
                                <label
                                    htmlFor="file-upload"
                                    className={`inline-block px-6 py-3 rounded-lg cursor-pointer transition-colors font-medium
                ${isUploading
                                            ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                            : 'bg-gradient-to-r from-[#F2C231] to-[#C69A32] text-white hover:from-[#C69A32] hover:to-[#F2C231]'
                                        }`}
                                >
                                    {isUploading ? 'Caricamento…' : 'Seleziona File'}
                                </label>
                            </div>

                            {/* Take Photo */}
                            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
                                <Camera className="h-10 w-10 text-gray-400 mx-auto mb-4" />
                                <p className="text-gray-600 mb-4">Usa la fotocamera del dispositivo</p>
                                <button
                                    onClick={startCamera}
                                    disabled={isUploading}
                                    className={`inline-block px-6 py-3 rounded-lg transition-colors font-medium
                ${isUploading
                                            ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                            : 'bg-gradient-to-r from-[#F2C231] to-[#C69A32] text-white hover:from-[#C69A32] hover:to-[#F2C231]'
                                        }`}
                                >
                                    Scatta Foto
                                </button>
                            </div>
                        </div>
                        <p className="text-sm text-gray-500 text-center mb-4">
                            Formati supportati: JPG, PNG, PDF (Max 5MB ciascuno)
                        </p>

                        {/* Uploaded Files List */}
                        {uploadedFiles.length > 0 && (
                            <div className="space-y-2">
                                <h4 className="font-medium text-gray-700 flex items-center">
                                    <FileText className="h-4 w-4 mr-2" />
                                    Documenti Caricati (X) ({uploadedFiles.length})
                                </h4>
                                <div className="max-h-60 overflow-y-auto space-y-2">
                                    {uploadedFiles.map((file) => (
                                        <div key={file.id} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg border">
                                            <div className="flex items-center flex-1 min-w-0">
                                                <FileText className="h-4 w-4 text-gray-500 mr-2 flex-shrink-0" />
                                                <span className="text-sm text-gray-800 truncate">{file.name}</span>
                                                <span className="text-xs text-gray-500 ml-2 flex-shrink-0">
                                                    ({formatFileSize(file.size)})
                                                </span>
                                            </div>
                                            <button
                                                onClick={() => removeDocument(file.id)}
                                                className="text-red-500 hover:text-red-700 p-1 ml-2 flex-shrink-0"
                                                title="Rimuovi documento"
                                            >
                                                <X className="h-4 w-4" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {isUploading && (
                        <div className="flex items-center justify-center py-4">
                            <Loader className="animate-spin text-yellow-500 h-6 w-6 mr-2" />
                            <span className="text-gray-600">Uploading documents...</span>
                        </div>
                    )}

                    {/* Navigation Buttons */}
                    <div className="mt-8 flex gap-4">
                        <Button
                            type="button"
                            onClick={() => navigate(`/services/${serviceId}/user-data`, {
                                state: { service }
                            })}
                            className="flex-1 py-3 rounded-lg font-bold text-lg transition-all duration-200
                                     bg-gray-200 text-gray-700 hover:bg-gray-300"
                        >
                            Indietro
                        </Button>

                        <Button
                            onClick={handleProceedToPayment}
                            disabled={isUploading}
                            className={`flex-1 py-3 rounded-lg font-bold text-lg transition-all duration-200
                                     ${!isUploading
                                    ? 'bg-gradient-to-r from-[#F2C231] to-[#C69A32] text-white hover:from-[#C69A32] hover:to-[#F2C231]'
                                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                }`}
                        >
                            {uploadedFiles.length > 0 ? 'Procedi al Pagamento' : 'Salta e Procedi al Pagamento'}
                        </Button>
                    </div>

                    {uploadedFiles.length === 0 && (
                        <p className="text-sm text-gray-500 mt-2 text-center">
                            Puoi procedere senza caricare documenti oppure caricarli ora
                        </p>
                    )}
                </div>
            </div>
            {/* Camera Modal */}
            {/* Camera Modal */}
            {showCameraModal && (
                <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
                        <div className="p-4 border-b flex justify-between items-center">
                            <h3 className="text-lg font-bold text-gray-800">
                                {capturedPhoto ? 'Review Photo' : 'Take Photo'}
                            </h3>
                            <button onClick={stopCamera} className="text-gray-500 hover:text-gray-700">
                                <X className="h-6 w-6" />
                            </button>
                        </div>

                        <div className="p-4">
                            <div className="relative bg-black rounded-lg overflow-hidden">
                                <video
                                    ref={videoRef}
                                    autoPlay
                                    playsInline
                                    className={`w-full h-auto ${capturedPhoto ? 'hidden' : 'block'}`}
                                />
                                <canvas
                                    ref={canvasRef}
                                    className={`w-full h-auto ${capturedPhoto ? 'block' : 'hidden'}`}
                                />
                            </div>

                            <div className="mt-4 flex gap-3">
                                {!capturedPhoto ? (
                                    <>
                                        <button
                                            onClick={stopCamera}
                                            className="flex-1 py-3 rounded-lg font-medium bg-gray-200 text-gray-700 hover:bg-gray-300"
                                        >
                                            Annulla
                                        </button>
                                        <button
                                            onClick={capturePhoto}
                                            className="flex-1 py-3 rounded-lg font-medium bg-gradient-to-r from-[#F2C231] to-[#C69A32] text-white hover:from-[#C69A32] hover:to-[#F2C231]"
                                        >
                                            Cattura
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <button
                                            onClick={retakePhoto}
                                            className="flex-1 py-3 rounded-lg font-medium bg-gray-200 text-gray-700 hover:bg-gray-300"
                                        >
                                            Riscatta
                                        </button>
                                        <button
                                            onClick={uploadCapturedPhoto}
                                            disabled={isUploading}
                                            className={`flex-1 py-3 rounded-lg font-medium
                                    ${isUploading
                                                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                                    : 'bg-gradient-to-r from-[#F2C231] to-[#C69A32] text-white hover:from-[#C69A32] hover:to-[#F2C231]'
                                                }`}
                                        >
                                            {isUploading ? 'Caricamento…' : 'Carica Foto'}
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <Toaster />

        </div>
    );
}